import React, { useEffect, useState } from "react";
import { Outlet, Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutGrid, Calendar, Users, MessageSquare, BarChart3, Crown, LogOut, User, Menu, X, Sparkles, Flame, Target, TrendingUp, GraduationCap } from "lucide-react";
import Wordmark from "@/components/Wordmark";
import { api } from "@/lib/api";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutGrid, testid: "nav-dashboard" },
  { to: "/review", label: "Daily Recall", icon: Flame, testid: "nav-review" },
  { to: "/spec", label: "Exam Spec", icon: Target, testid: "nav-spec" },
  { to: "/progress", label: "Progress", icon: TrendingUp, testid: "nav-progress" },
  { to: "/timetable", label: "Timetable", icon: Calendar, testid: "nav-timetable" },
  { to: "/groups", label: "Groups", icon: Users, testid: "nav-groups" },
  { to: "/classes", label: "Classes", icon: GraduationCap, testid: "nav-classes" },
  { to: "/coach", label: "AI Coach", icon: MessageSquare, testid: "nav-coach" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
];

const PlanBadge = ({ plan }) => {
  const map = {
    free:    { cls: "border-white/10 text-neutral-400 bg-white/[0.02]", label: "FREE" },
    starter: { cls: "border-amber-500/40 text-amber-400 bg-amber-500/[0.06]", label: "STARTER" },
    pro:     { cls: "border-rose-500/40 text-rose-400 bg-rose-500/[0.06]", label: "PRO" },
  };
  const m = map[plan] || map.free;
  return (
    <span
      className={`text-[9px] font-mono font-medium tracking-[0.2em] px-1.5 py-0.5 border rounded-[2px] ${m.cls}`}
      data-testid="plan-badge"
    >
      {m.label}
    </span>
  );
};

const AppLayout = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [inviteCount, setInviteCount] = useState(0);
  const [dueCount, setDueCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/invites/count");
        if (!cancelled) setInviteCount(data?.count || 0);
      } catch { /* ignore */ }
      try {
        const { data } = await api.get("/review/today", { params: { limit: 1 } });
        if (!cancelled) setDueCount(data?.due_count || 0);
      } catch { /* ignore */ }
    };
    load();
    const i = setInterval(load, 45000);
    // refresh when the tab regains focus
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    // let child pages force a refresh via CustomEvent
    const onCustom = () => load();
    window.addEventListener("vertisiya:invites-changed", onCustom);
    return () => {
      cancelled = true;
      clearInterval(i);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("vertisiya:invites-changed", onCustom);
    };
  }, []);

  return (
    <div className="min-h-screen flex relative bg-black text-white">
      {/* ambient aurora far behind */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="holo-mesh opacity-40" aria-hidden />
      </div>

      {/* sidebar */}
      <aside
        className={`fixed md:sticky top-0 z-30 w-[240px] h-screen flex-col shrink-0 bg-black/70 backdrop-blur-2xl border-r border-white/[0.06] ${open ? "flex" : "hidden md:flex"}`}
      >
        <div className="p-5 border-b border-white/[0.06] flex items-center justify-between">
          <Link to="/dashboard" data-testid="sidebar-brand"><Wordmark size="sm" /></Link>
          <button className="md:hidden text-neutral-500 hover:text-white" onClick={() => setOpen(false)} data-testid="sidebar-close">
            <X size={16} />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          <div className="label-mono px-3 pt-2 pb-3 text-[0.62rem]">/ workspace</div>
          {navItems.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2 text-[0.86rem] rounded-[2px] transition-colors ${
                    isActive
                      ? "bg-white/[0.05] text-white border border-white/[0.08]"
                      : "text-neutral-400 hover:text-white hover:bg-white/[0.03] border border-transparent"
                  }`
                }
              >
                <Icon size={15} className="shrink-0" />
                <span className="tracking-tight">{n.label}</span>
                {n.to === "/groups" && inviteCount > 0 && (
                  <span
                    className="ml-auto text-[10px] font-mono font-medium tabular tracking-wider text-black bg-amber-400 px-1.5 py-0.5 rounded-[2px] shadow-[0_0_12px_rgba(245,158,11,0.55)]"
                    data-testid="nav-invite-badge"
                  >
                    {inviteCount > 99 ? "99+" : inviteCount}
                  </span>
                )}
                {n.to === "/coach" && user?.plan === "free" && (
                  <span className="ml-auto text-[9px] label-mono text-amber-500">PRO</span>
                )}
                {n.to === "/review" && dueCount > 0 && (
                  <span
                    className="ml-auto text-[10px] font-mono font-medium tabular tracking-wider text-black bg-amber-400 px-1.5 py-0.5 rounded-[2px] shadow-[0_0_12px_rgba(245,158,11,0.55)]"
                    data-testid="nav-due-badge"
                  >
                    {dueCount > 99 ? "99+" : dueCount}
                  </span>
                )}
                {n.to === "/classes" && user?.plan !== "pro" && (
                  <span className="ml-auto text-[9px] label-mono text-rose-400">PRO</span>
                )}
                {n.to === "/analytics" && user?.plan !== "pro" && (
                  <span className="ml-auto text-[9px] label-mono text-rose-400">PRO</span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* upgrade tile */}
        {user?.plan === "free" && (
          <div className="p-3">
            <Link to="/pricing" data-testid="sidebar-upgrade" className="block card-panel p-4 hover:border-white/20 transition-colors">
              <div className="flex items-center gap-2 mb-1.5">
                <Sparkles size={13} className="text-amber-500" />
                <span className="text-[0.78rem] font-medium">Upgrade</span>
              </div>
              <p className="text-[0.72rem] text-neutral-500 leading-relaxed mb-3">
                Unlock the Coach, unlimited recordings & advanced analytics.
              </p>
              <div className="btn-primary text-[0.75rem] py-1.5 px-3 w-full inline-flex items-center justify-center gap-1">
                <Crown size={12} /> See plans
              </div>
            </Link>
          </div>
        )}

        {/* account footer */}
        <div className="p-3 border-t border-white/[0.06]">
          <Link to="/account" className="flex items-center gap-3 p-2 rounded-[2px] hover:bg-white/[0.03] transition-colors group" data-testid="sidebar-account">
            <div className="w-8 h-8 bg-white/[0.05] border border-white/[0.08] flex items-center justify-center rounded-[2px]">
              <User size={13} className="text-neutral-400 group-hover:text-white transition-colors" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[0.82rem] truncate text-white leading-tight">{user?.display_name}</div>
              <div className="text-[0.68rem] text-neutral-500 truncate">@{user?.username}</div>
            </div>
            <PlanBadge plan={user?.plan} />
          </Link>
          <button
            onClick={() => { logout(); nav("/"); }}
            className="mt-2 w-full text-[0.75rem] text-neutral-500 hover:text-white flex items-center gap-2 px-3 py-2 transition-colors"
            data-testid="sidebar-logout"
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </aside>

      {/* mobile toggle */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="md:hidden fixed top-4 left-4 z-40 glass p-2 rounded-[2px]"
          data-testid="sidebar-toggle"
        >
          <Menu size={16} />
        </button>
      )}

      {/* mobile backdrop */}
      {open && (
        <div className="fixed inset-0 z-20 bg-black/70 backdrop-blur-sm md:hidden" onClick={() => setOpen(false)} />
      )}

      {/* main */}
      <main className="flex-1 relative z-10 min-w-0">
        <Outlet />
      </main>
    </div>
  );
};

export default AppLayout;
